/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : CustomMultiSelectAutocompleteWithApiV2.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import React, { useEffect, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  Checkbox,
  CircularProgress,
  TextField,
  InputAdornment,
  IconButton,
  Button,
} from "@mui/material";
import { useDispatch } from "react-redux";
import { HandleApiActions } from "../../../../redux/actions";
import CloseIcon from "@mui/icons-material/Close";


const CustomMultiSelectAutocompleteWithApiV2 = ({
  apiAction,
  params = {},
  searchKey,
  mapResponseToOptions,
  selectedValues = [],
  setSelectedValues,
  size = "small",
  variant = "outlined",
  placeholder = "Search...",
  txtFldStyle = {},
  paperSx = {},
  skipInitialSearch = false,
  triggerApi,
  enableSelectSimilar = false,
  enableSelectAll = false,
}) => {
  const dispatch = useDispatch();
  const isFirstRun = useRef(true);
  const prevParamsRef = useRef(params);

  const [searchText, setSearchText] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const normalizeOptionLabel = (label) =>
    String(label || "")
      .trim()
      .toLowerCase();

  const getSimilarOptions = (option) => {
    const selectedLabel = normalizeOptionLabel(option?.label);
    if (!selectedLabel) return [];

    return (options || []).filter(
      (item) => normalizeOptionLabel(item?.label) === selectedLabel,
    );
  };

  const areSimilarOptionsSelected = (option) => {
    const similarOptions = getSimilarOptions(option);
    if (!similarOptions.length) return false;

    return similarOptions.every((similarOption) =>
      (selectedValues || []).some(
        (selectedOption) => selectedOption?.value === similarOption?.value,
      ),
    );
  };

  const handleToggleSimilar = (event, option) => {
    event.preventDefault();
    event.stopPropagation();

    const matchingOptions = getSimilarOptions(option);
    if (!matchingOptions.length) return;

    setSelectedValues?.((prev) => {
      const selectedList = Array.isArray(prev) ? prev : [];
      const isAllMatchingSelected = matchingOptions.every((matchingOption) =>
        selectedList.some(
          (selectedOption) => selectedOption?.value === matchingOption?.value,
        ),
      );

      if (isAllMatchingSelected) {
        const matchingValueSet = new Set(
          matchingOptions.map((item) => item?.value),
        );

        return selectedList.filter(
          (selectedOption) => !matchingValueSet.has(selectedOption?.value),
        );
      }

      const selectedMap = new Map(
        selectedList.map((item) => [item?.value, item]),
      );

      matchingOptions.forEach((item) => {
        selectedMap.set(item?.value, item);
      });

      return Array.from(selectedMap.values());
    });
  };

  const areAllOptionsSelected = () => {
    const optionList = Array.isArray(options) ? options : [];
    if (!optionList.length) return false;

    return optionList.every((option) =>
      (selectedValues || []).some(
        (selectedOption) => String(selectedOption?.value) === String(option?.value),
      ),
    );
  };

  const handleToggleAllOptions = (event) => {
    const isChecked = event.target.checked;
    const optionList = Array.isArray(options) ? options : [];

    setSelectedValues?.((prev) => {
      const selectedList = Array.isArray(prev) ? prev : [];
      const optionValueSet = new Set(optionList.map((item) => String(item?.value)));

      if (!isChecked) {
        return selectedList.filter(
          (selectedOption) => !optionValueSet.has(String(selectedOption?.value)),
        );
      }

      const selectedMap = new Map(
        selectedList.map((item) => [String(item?.value), item]),
      );

      optionList.forEach((item) => {
        selectedMap.set(String(item?.value), item);
      });

      return Array.from(selectedMap.values());
    });
  };

  const handleSearch = async (query) => {
    setLoading(true);
    try {
      const response = await dispatch(
        HandleApiActions({
          ...apiAction,
          params: {
            ...params,
            ...(query?.trim() && searchKey
              ? { [searchKey]: query.trim() }
              : {}),
            ignore_app_loading_spinner: true,
          },
          show_toast: false,
        })
      );

      if (!response?.error) {
        const mapped =
          typeof mapResponseToOptions === "function"
            ? mapResponseToOptions(response?.data, response)
            : (response?.data || []).map((it) => ({
              value: it?.id ?? it?.value,
              label: it?.name ?? it?.label ?? "",
            }));
        setOptions(mapped || []);
      } else {
        setOptions([]);
      }
    } catch (err) {
      console.error("MultiSelect API search error:", err);
      setOptions([]);
    } finally {
      setLoading(false);
    }
  };

  // Debounce search on input change
  useEffect(() => {
    const timer = setTimeout(() => handleSearch(searchText), 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText]);

  // Initial fetch and params change
  useEffect(() => {
    const paramsChanged =
      JSON.stringify(prevParamsRef.current) !== JSON.stringify(params);

    if (isFirstRun.current) {
      if (!skipInitialSearch) handleSearch(searchText);
      isFirstRun.current = false;
    } else if (paramsChanged) {
      handleSearch(searchText);
    }

    prevParamsRef.current = params;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, skipInitialSearch]);

  // External trigger
  useEffect(() => {
    if (!isFirstRun.current) handleSearch(searchText);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerApi]);

  // No auto-open: open only on focus/typing/select actions

  return (
    <>
      {enableSelectAll && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            height: 0,
            mb: 0,
            transform: "translateY(-25px)",
            fontSize: "13px",
            color: "#232323",
            width: "100%",
            position: "relative",
            zIndex: 1,
          }}
        >
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
            }}
          >
            <Checkbox
              size="small"
              checked={areAllOptionsSelected()}
              disabled={!options?.length}
              onChange={handleToggleAllOptions}
              sx={{
                p: 0,
                mr: 0.75,
                color: "#000",
                "&.Mui-checked": { color: "#000" },
              }}
            />
            Select All
          </Box>
        </Box>
      )}
      <Autocomplete
      multiple
      disableCloseOnSelect
      clearOnBlur={false}
      openOnFocus
      disableClearable
      open={!!isOpen}
      onOpen={() => setIsOpen(true)}
      onClose={(event, reason) => {
        // Keep open when selecting options (multi-select), otherwise close
        if (reason === "selectOption") {
          setIsOpen(true);
        } else {
          setIsOpen(false);
        }
      }}
      options={options || []}
      value={Array.isArray(selectedValues) ? selectedValues : []}
      onChange={(event, newValue) => {
        setSelectedValues?.(Array.isArray(newValue) ? newValue : []);
      }}
      inputValue={searchText ?? ""}
      onInputChange={(event, newInputValue, reason) => {
        // Prevent MUI from resetting input on option update or internal resets
        if (reason === "reset" || reason === "clear") return;
        if (reason === "input") setIsOpen(true);
        setSearchText(newInputValue || "");
      }}
      getOptionLabel={(option) => option?.label || ""}
      isOptionEqualToValue={(option, value) => option?.value === value?.value}
      filterOptions={(opts) => opts} // server-side search; do not client-filter
      loading={loading}
      renderOption={(props, option, { selected }) => (
        <Box
          component="li"
          {...props}
          sx={{
            display: "flex",
            alignItems: "center",
            cursor: "pointer",
            p: "8px 12px !important",
            "&:hover": { backgroundColor: "#f5f5f5" },
            "&:hover .select-similar-button": {
              opacity: 1,
              pointerEvents: "auto",
            },
          }}
        >
          <Checkbox
            checked={selected}
            sx={{
              marginRight: 1,
              color: "#000",
              "&.Mui-checked": { color: "#000" },
            }}
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>{option?.label}</Box>
          {enableSelectSimilar && (
            <Button
              className="select-similar-button"
              size="small"
              variant="text"
              onClick={(event) => handleToggleSimilar(event, option)}
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
              sx={{
                minWidth: "auto",
                ml: 1,
                px: 1,
                py: 0,
                opacity: 0,
                pointerEvents: "none",
                color: "#26890D",
                fontSize: "12px",
                textTransform: "none",
              }}
            >
              {areSimilarOptionsSelected(option) ? "Unselect all" : "Select all"}
            </Button>
          )}
        </Box>
      )}
      renderTags={() => null}
      renderInput={(params) => (
        <TextField
          {...params}
          variant={variant}
          size={size}
          placeholder={placeholder}
          onKeyDown={(e) => {
            // Prevent Backspace from removing last selected option when input is empty
            if (e.key === "Backspace" && (searchText || "").length === 0) {
              e.stopPropagation();
            }
          }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? (
                  <CircularProgress color="inherit" size={16} />
                ) : null}
                <InputAdornment
                  position="end"
                  sx={{
                    width: 32,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  {!!searchText && (
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setSearchText("");
                        setIsOpen(true);
                      }}
                    >
                      <CloseIcon sx={{ color: "black" }} />
                    </IconButton>
                  )}
                </InputAdornment>
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              height: 32,
              borderRadius: 0,
              "&:hover fieldset": { borderColor: "#000000" },
              "&.Mui-focused fieldset": { borderColor: "#000000" },
            },
            ...txtFldStyle,
          }}
        />
      )}
      slotProps={{
        paper: {
          sx: {
            maxHeight: 250,
            overflow: "hidden",
            "& .MuiAutocomplete-listbox": {
              maxHeight: 250,
              overflowY: "auto",
              scrollbarColor: "#888 #f1f1f1",
              scrollbarWidth: "thin",
              "&::-webkit-scrollbar": { width: "8px", height: "8px" },
              "&::-webkit-scrollbar-track": { backgroundColor: "#f1f1f1" },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#888",
                borderRadius: "4px",
              },
              "&::-webkit-scrollbar-thumb:hover": { backgroundColor: "#555" },
            },
            ...paperSx,
          },
        },
      }}
      />
    </>
  );
};

export default CustomMultiSelectAutocompleteWithApiV2;
